/**
 * Reusable SEO landing page for California ADU markets.
 * Each market (LA, San Diego, Bay Area) is a small data file
 * that drives this template — high SEO surface, zero duplication.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle, MapPin, ShieldCheck, Sparkles, Building2, Clock, AlertTriangle } from "lucide-react";
import BlueprintBackdrop from "./BlueprintBackdrop";
import BrandWordmark from "./BrandWordmark";
import SEOHead from "./SEOHead";
import CountyNavLinks from "./CountyNavLinks";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SITE = "https://www.plangeneratorca.com";

const ALL_COUNTIES = [
  { slug: "los-angeles", region: "Los Angeles", path: "/california/los-angeles-adu-permit", blurb: "LADBS + LA County Public Works portal." },
  { slug: "san-diego", region: "San Diego", path: "/california/san-diego-adu-permit", blurb: "SDMC §142, DSD, EDCO + Republic." },
  { slug: "bay-area", region: "Bay Area", path: "/california/bay-area-adu-permit", blurb: "9 counties incl. SF, Alameda, Santa Clara." },
];

const CountyLandingPage = ({ data }) => {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState({
    cwmp_price: 59,
    bundle_price: 89,
    subscription_price: 99,
  });

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const r = await fetch(`${API}/pricing`);
        if (r.ok) {
          const d = await r.json();
          setPricing((p) => ({ ...p, ...d }));
        }
      } catch {
        // fall back to defaults
      }
    };
    fetchPricing();
  }, []);

  // Build JSON-LD: LocalBusiness + FAQPage + Service
  const path = `/california/${data.slug}-adu-permit`;
  const fullUrl = `${SITE}${path}`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: `Plan Generator CA — ${data.region}`,
      url: fullUrl,
      description: `California ADU Permit Toolkit for ${data.region} — CALGreen CWMPs, Title 24 notes, and AI-generated compliance memos for ${data.region} contractors.`,
      areaServed: { "@type": "Place", name: `${data.region}, California` },
      address: {
        "@type": "PostalAddress",
        addressRegion: "CA",
        addressCountry: "US",
        addressLocality: data.region,
      },
      priceRange: `$${pricing.cwmp_price}–$${pricing.subscription_price}/mo`,
      image: `${SITE}/og-image.svg`,
      sameAs: [SITE],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: data.faqs.map((q) => ({
        "@type": "Question",
        name: q.q,
        acceptedAnswer: { "@type": "Answer", text: q.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: `${data.region} ADU Permit Documentation`,
      name: `${data.region} ADU Permit Toolkit`,
      provider: { "@type": "Organization", name: "Plan Generator CA" },
      areaServed: { "@type": "Place", name: `${data.region}, California` },
      offers: {
        "@type": "AggregateOffer",
        priceCurrency: "USD",
        lowPrice: pricing.cwmp_price,
        highPrice: pricing.subscription_price,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE },
        { "@type": "ListItem", position: 2, name: "California", item: `${SITE}/` },
        { "@type": "ListItem", position: 3, name: `${data.region} ADU Permit Toolkit`, item: fullUrl },
      ],
    },
  ];

  const otherCounties = ALL_COUNTIES.filter((c) => c.slug !== data.slug);

  return (
    <div className="min-h-screen bg-background" data-testid={`county-page-${data.slug}`}>
      <SEOHead
        title={`${data.region} ADU Permit Toolkit — CALGreen + AI Compliance Memo`}
        description={`Get your ${data.region} ADU permit approved on the first submittal. CALGreen-compliant CWMP, Title 24 notes, and an AI-generated cover memo for ${data.region} plan checkers — council-ready in under 10 minutes.`}
        keywords={`${data.region} ADU permit, ${data.region} CALGreen, ${data.region} ADU compliance, ${data.region} CWMP, accessory dwelling unit ${data.region}, California ADU toolkit`}
        path={path}
        image={`${SITE}/og-image.svg`}
        jsonLd={jsonLd}
      />
      {/* Top nav */}
      <nav className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-5">
              <BrandWordmark size="md" onClick={() => navigate("/")} />
              <CountyNavLinks className="hidden lg:flex" />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate("/")}
                className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                data-testid="county-back-home"
              >
                <ArrowLeft className="w-4 h-4" /> Home
              </button>
              <button
                onClick={() => navigate("/register")}
                className="pill-btn pill-btn-primary text-xs px-4 py-2"
                data-testid="county-nav-cta"
              >
                Get started
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-16 pb-16 px-4 overflow-hidden">
        <BlueprintBackdrop variant="hero" showIsometric={true} />
        <div className="relative max-w-5xl mx-auto z-10">
          <div className="pre-headline-pill mb-5 inline-flex">
            <MapPin className="w-3 h-3" />
            {data.region} · ADU Permit Toolkit
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-foreground tracking-tight leading-[0.95] max-w-4xl">
            {data.h1Prefix}{" "}
            <span className="headline-accent">{data.h1Accent}</span>
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-2xl leading-relaxed">
            {data.subtitle}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate("/register")}
              data-testid="county-hero-cta"
              className="pill-btn pill-btn-primary text-base px-7 py-3.5"
            >
              Start your plan
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate("/sample-memo")}
              className="pill-btn pill-btn-secondary text-base px-7 py-3.5"
            >
              See a sample memo
            </button>
          </div>

          <div className="mt-10 grid sm:grid-cols-3 gap-3 max-w-2xl">
            {data.heroStats.map((s, i) => (
              <div key={i} className="surface-card p-4">
                <div className="font-display font-black text-2xl text-foreground">{s.value}</div>
                <div className="text-xs text-muted-foreground mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Local rules */}
      <section className="py-20 px-4 border-t border-border/60 bg-muted/30">
        <div className="max-w-5xl mx-auto">
          <span className="pre-headline-pill mb-4">Local requirements</span>
          <h2 className="font-display font-black text-3xl sm:text-4xl text-foreground tracking-tight max-w-3xl">
            What {data.region} ADU contractors actually need to submit.
          </h2>
          <div className="mt-10 grid md:grid-cols-2 gap-5">
            {data.localRules.map((rule, i) => (
              <div key={i} className="surface-card p-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-accent/10 text-accent flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-base text-foreground">{rule.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{rule.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What you get */}
      <section className="py-20 px-4 border-t border-border/60">
        <div className="max-w-5xl mx-auto">
          <span className="pre-headline-pill mb-4">What you get</span>
          <h2 className="font-display font-black text-3xl sm:text-4xl text-foreground tracking-tight max-w-3xl">
            One toolkit, every document your <span className="headline-accent">{data.region}</span> plan checker wants.
          </h2>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            <div className="surface-card p-6">
              <Building2 className="w-7 h-7 text-accent mb-3" />
              <h3 className="font-display font-bold text-lg text-foreground">CALGreen CWMP</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                Pre-construction waste management plan with auto-calculated 65% diversion math, county-specific facility list, and ${pricing.cwmp_price} per report.
              </p>
            </div>
            <div className="surface-card p-6">
              <Sparkles className="w-7 h-7 text-accent mb-3" />
              <h3 className="font-display font-bold text-lg text-foreground">AI Compliance Memo</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                Plain-English cover letter citing CALGreen §5.408, Title 24 Part 11, and {data.region}-specific portal references. Stamped in 60 seconds.
              </p>
            </div>
            <div className="surface-card p-6">
              <ShieldCheck className="w-7 h-7 text-accent mb-3" />
              <h3 className="font-display font-bold text-lg text-foreground">Final Verification</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                Post-construction report with receipt tracking and variance analysis — required for certificate-of-occupancy signoff.
              </p>
            </div>
          </div>

          <div className="mt-10 surface-card p-6 max-w-3xl flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Bundle (most popular)</div>
              <div className="font-display font-black text-3xl text-foreground mt-1">
                ${pricing.bundle_price}<span className="text-base text-muted-foreground font-normal"> · CWMP + Final + unlimited memos</span>
              </div>
            </div>
            <button
              onClick={() => navigate("/register")}
              className="pill-btn pill-btn-accent"
              data-testid="county-pricing-cta"
            >
              Get started
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Why it matters */}
      <section className="py-20 px-4 border-t border-border/60 bg-muted/30">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-10 items-start">
          <div>
            <span className="pre-headline-pill pre-headline-pill-warm mb-4 inline-flex">
              <AlertTriangle className="w-3 h-3" />
              The $1,500 problem
            </span>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-foreground tracking-tight">
              Why {data.region} ADU permits get rejected.
            </h2>
            <ul className="mt-6 space-y-3 text-foreground/90">
              {data.painPoints.map((p, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <span className="w-5 h-5 rounded-full bg-[#FCD9D6] text-[#8B1F1F] inline-flex items-center justify-center text-[0.65rem] font-bold flex-shrink-0">×</span>
                  {p}
                </li>
              ))}
            </ul>
          </div>
          <div className="surface-card p-7">
            <Clock className="w-8 h-8 text-accent mb-3" />
            <h3 className="font-display font-bold text-xl text-foreground">10 minutes to council-ready</h3>
            <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
              Most {data.region} contractors finish their CWMP and AI compliance memo in under ten minutes — before they finish their first cup of coffee. No consultants. No re-submittals. No $1,500 inspection-failure tax.
            </p>
            <button
              onClick={() => navigate("/register")}
              className="pill-btn pill-btn-primary mt-6"
              data-testid="county-cta-bottom"
            >
              Get started now
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 border-t border-border/60">
        <div className="max-w-3xl mx-auto">
          <span className="pre-headline-pill mb-4">FAQ · {data.region}</span>
          <h2 className="font-display font-black text-3xl sm:text-4xl text-foreground tracking-tight">
            Quick answers for {data.region} ADU builders.
          </h2>
          <div className="mt-8 space-y-3">
            {data.faqs.map((q, i) => (
              <details key={i} className="surface-card p-5 cursor-pointer group" data-testid={`county-faq-${i}`}>
                <summary className="font-display font-semibold text-foreground list-none flex items-center justify-between">
                  {q.q}
                  <span className="text-accent transition-transform group-open:rotate-45 text-xl leading-none">+</span>
                </summary>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{q.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Related county guides — internal SEO mesh */}
      <section className="py-16 px-4 border-t border-border/60 bg-muted/30">
        <div className="max-w-5xl mx-auto">
          <span className="pre-headline-pill mb-4">More California guides</span>
          <h2 className="font-display font-black text-2xl sm:text-3xl text-foreground tracking-tight">
            Working in another California market?
          </h2>
          <div className="mt-6 grid sm:grid-cols-3 gap-4">
            {otherCounties.map((c) => (
              <button
                key={c.slug}
                onClick={() => navigate(c.path)}
                className="surface-card p-5 text-left hover:shadow-md transition-shadow"
                data-testid={`related-county-${c.slug}`}
              >
                <div className="text-[0.65rem] uppercase tracking-wider text-accent font-bold mb-1">{c.region}</div>
                <h3 className="font-display font-bold text-base text-foreground">{c.region} ADU Toolkit</h3>
                <p className="text-xs text-muted-foreground mt-1.5">{c.blurb}</p>
                <span className="text-xs text-accent font-semibold mt-3 inline-flex items-center gap-1">
                  Open guide <ArrowRight className="w-3 h-3" />
                </span>
              </button>
            ))}
            <button
              onClick={() => navigate("/sample-memo")}
              className="surface-card p-5 text-left hover:shadow-md transition-shadow sm:col-span-3"
              data-testid="related-sample-memo"
            >
              <div className="text-[0.65rem] uppercase tracking-wider text-accent font-bold mb-1">Sample</div>
              <h3 className="font-display font-bold text-base text-foreground">See a live AI compliance memo for an LA ADU</h3>
              <p className="text-xs text-muted-foreground mt-1.5">Generated end-to-end from real project data — the exact format your plan checker receives.</p>
              <span className="text-xs text-accent font-semibold mt-3 inline-flex items-center gap-1">
                View sample memo <ArrowRight className="w-3 h-3" />
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 border-t border-border/60 relative overflow-hidden">
        <BlueprintBackdrop variant="panel" showIsometric={false} />
        <div className="relative max-w-3xl mx-auto text-center z-10">
          <span className="pre-headline-pill mb-4 inline-flex">Ready when you are</span>
          <h2 className="font-display font-black text-3xl sm:text-4xl text-foreground tracking-tight">
            Approve your {data.region} ADU permit on the <span className="headline-accent">first try.</span>
          </h2>
          <p className="mt-4 text-base text-muted-foreground">
            CALGreen + Title 24 + AI memo, ready to staple onto your permit package. Pay only for plans you submit.
          </p>
          <button
            onClick={() => navigate("/register")}
            className="pill-btn pill-btn-primary mt-7 text-base px-7 py-3.5"
            data-testid="county-final-cta"
          >
            Create account
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-4 border-t border-border/60 bg-muted/30">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <BrandWordmark size="sm" onClick={() => navigate("/")} />
          <p className="text-xs text-muted-foreground text-center sm:text-right">
            © {new Date().getFullYear()} Plan Generator CA · Serving all 58 California counties · {data.region}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CountyLandingPage;
